/**
 * ContributionTilesSection.tsx
 *
 * Renders a section of contribution tiles for the Contribution Portal.
 * Each tile represents an action or link (e.g. create recipe, read guide, get help).
 *
 * Props:
 * - tiles: ContributionTileType[] - Array of tile configuration objects.
 * - ariaLabel?: string - Optional ARIA label for accessibility.
 */

import React from "react";
import { Box, Typography, Paper, SxProps } from "@mui/material";
import ContributionTile from "./ContributionTile";
import { ContributionTile as ContributionTileType } from "./ContributionTilesConfig";
import { useTheme } from "@mui/material/styles";

interface Tile {
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
  target?: string;
  sx?: SxProps;
}

interface Props {
  tiles: ContributionTileType[];
  ariaLabel?: string;
}

/**
 * ContributionTilesSection component.
 * Displays a set of ContributionTile components in a responsive layout.
 */
const ContributionTilesSection: React.FC<Props> = ({ tiles }) => {
  const theme = useTheme();
  return (
    <Box sx={{ flex: "1 1 0px" }}>
      <Paper sx={{ p: 3, borderRadius: 0.5, boxShadow: "none" }}>
        {/* Section title */}
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.materiom.seaweed,
            fontWeight: "bold",
            mb: 2,
          }}
        >
          Make a contribution
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" }, // stack vertically on xs/sm
            flexWrap: "wrap",
            gap: { xs: 0, md: 0 }, // smaller gap on small screens
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          {/* Render each contribution tile */}
          {tiles.map((tile, index) => (
            <Box
              key={index}
              sx={{
                flex: { xs: "1 1 140px", sm: "1 1 180px", md: "0 1 220px" },
                minWidth: { xs: 120, sm: 160, md: 180 },
                maxWidth: { xs: "100%", md: 220 },
                mx: 2,
              }}
            >
              <ContributionTile
                icon={
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    {/* Render icon, resizing if it's a React element */}
                    {React.isValidElement(tile.icon) ? (
                      React.cloneElement(tile.icon, {})
                    ) : (
                      <Box
                        sx={{
                          width: "70%",
                          height: "70%",
                        }}
                      >
                        {tile.icon}
                      </Box>
                    )}
                  </Box>
                }
                label={tile.label}
                href={tile.href}
                onClick={tile.onClick}
                target={tile.target}
                sx={tile.sx}
              />
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default ContributionTilesSection;
